import { VendorDetailHeader } from './components/organisms/VendorDetailHeader';
import { AboutSection } from './components/organisms/AboutSection';
import { ServicesSection } from './components/organisms/ServicesSection';
import { RiskSummarySection } from './components/organisms/RiskSummarySection';
import { ProductsSection } from './components/organisms/ProductsSection';
import type { VendorData, VendorProduct } from './types';
import type { RiskLevel } from '../vendors-v2/types';
import type { ProductActionType } from './types/product-actions.types';

interface VendorDetailPageContentProps {
  vendorData: VendorData;
  memberIdNameMapping?: Record<string, string>;
  onNewAssessment?: () => void;
  onViewAssessment?: (productId: string) => void;
  onProductAction?: (action: ProductActionType, product: VendorProduct) => void;
}

/**
 * Maps API risk_rating string to RiskLevel type
 */
const mapRiskLevel = (riskRating: string | null): RiskLevel | null => {
  if (!riskRating) return null;
  const normalized = riskRating.trim();
  if (normalized === 'Low' || normalized === 'Medium' || normalized === 'High') {
    return normalized;
  }
  return null;
};

/**
 * Gets the most recent updated_at date from products for the "Updated" timestamp
 */
const getMostRecentUpdate = (vendorData: VendorData): string | null => {
  const products = vendorData.products;
  if (!products || products.length === 0) return null;

  const dates = products
    .map((p) => p.updated_at)
    .filter((d): d is string => !!d)
    .sort((a, b) => new Date(b).getTime() - new Date(a).getTime());

  return dates[0] || null;
};

/**
 * VendorDetailPageContent - Renders vendor detail sections with mapped API data
 */
export default function VendorDetailPageContent({
  vendorData,
  memberIdNameMapping = {},
  onNewAssessment,
  onViewAssessment,
  onProductAction,
}: VendorDetailPageContentProps) {
  // Map API data to component props
  const riskLevel = mapRiskLevel(vendorData.risk_rating);
  const updatedAt = getMostRecentUpdate(vendorData);

  // Map company info
  const companyInfo = {
    name: vendorData.company_name,
    founded: vendorData.founded,
    headquarters: vendorData.headquarters,
    employees: vendorData.no_of_employees,
    ownership: vendorData.ownership,
    domain: vendorData.primary_domain,
  };

  // Map intelligence counts
  const intelligence = {
    breachHistory: vendorData.total_breaches,
    darkWebChatter: vendorData.total_dark_web_chatter_findings,
    compliance: vendorData.total_compliances,
    news: vendorData.total_news_articles,
  };

  // Map risk posture
  const riskPostureScore = vendorData.vendor_risk_posture?.score ?? null;
  const riskPostureMax = vendorData.vendor_risk_posture?.max ?? 100;

  return (
    <div className="space-y-4">
      {/* Header */}
      <VendorDetailHeader
        vendorName={vendorData.vendor_name}
        logoUrl={vendorData.vendor_logo_url}
        riskLevel={riskLevel}
        websiteUrl={vendorData.primary_domain}
        onNewAssessment={onNewAssessment}
      />

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-4 items-start">
        {/* Left Column */}
        <div className="flex flex-col gap-4 h-[600px]">
          <AboutSection
            vendorName={vendorData.vendor_name}
            updatedAt={updatedAt}
            companyInfo={companyInfo}
            companyInformation={vendorData.company_information}
            servicesAndProducts={null}
          />
          <ServicesSection servicesAndProducts={vendorData.services_and_products} />
        </div>

        {/* Right Column: Risk Summary */}
        <div>
          <RiskSummarySection
            overallRisk={riskLevel}
            riskJustification={vendorData.risk_justification}
            riskPostureScore={riskPostureScore}
            riskPostureMax={riskPostureMax}
            intelligence={intelligence}
            breachHistoryEvents={vendorData.breach_history_events}
            darkWebChatterFindings={vendorData.dark_web_chatter_findings}
            complianceItems={vendorData.compliances}
            newsArticles={vendorData.news_articles}
          />
        </div>
      </div>

      {/* Products Section */}
      <ProductsSection
        products={vendorData.products}
        memberIdNameMapping={memberIdNameMapping}
        onViewAssessment={onViewAssessment}
        onProductAction={onProductAction}
        onNewAssessment={onNewAssessment}
      />
    </div>
  );
}
